import React, { useEffect, useState } from 'react'
import SlideBase from './SlideBase'
import FormatCurrency from 'components/Common/FormatCurrency/FormatCurrency'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import FadeBackground from '../FadeBackground'
import { useWrappedStorySelector } from 'contexts/Wrapped'
import { WRAPPED_SLIDES } from '../../config/slides'
import useVideoBackgroundWithFallback from '../../hooks/useVideoBackgroundWithFallback'

// Get slide config for this slide (static, doesn't depend on runtime data)
const SLIDE_CONFIG = WRAPPED_SLIDES.find(s => s.id === 'savings')
const SLIDE_ID = 'savings' as const

function SavingsSlide() {
  // Use granular selectors to minimize re-renders
  const data = useWrappedStorySelector(state => state.data)
  const totalsData = useWrappedStorySelector(state => state.totalsData)

  // Use the video background with fallback hook
  const { background, isBackgroundReady, isCurrentSlide } = useVideoBackgroundWithFallback({
    slideConfig: SLIDE_CONFIG,
    slideId: SLIDE_ID,
  })

  const [animate, setAnimate] = useState(!SLIDE_CONFIG?.video) // If no video, animate immediately

  // Totals-only mode is determined by not having user wrapped data
  const isTotalsOnlyMode = !data

  // Start animation when background is ready (video loaded or fallback image)
  useEffect(() => {
    if (SLIDE_CONFIG?.video && isBackgroundReady && isCurrentSlide) {
      setAnimate(true)
    }
  }, [isBackgroundReady, isCurrentSlide])

  // Need either user data or totals data to render
  if (!data && !totalsData) return null

  const userSavings = data?.stats.totalSavings ?? 0
  const userCurrency = data?.stats.currency ?? 'AUD'
  const totalsSavings = totalsData?.stats.totalSavings ?? 0
  const totalsCurrency = totalsData?.stats.currency ?? userCurrency
  const isLowSavings = userSavings < 100
  const useTotalsFallback = isTotalsOnlyMode || isLowSavings

  const heading = useTotalsFallback
    ? 'In 2025, Luxury Escapes travellers saved a total of'
    : 'With Luxury Escapes, you saved a total of'

  const savingsValue = useTotalsFallback ? totalsSavings : userSavings
  const savingsCurrency = useTotalsFallback ? totalsCurrency : userCurrency
  const useCompactFormat = savingsValue >= 1_000_000
  const dailyTotalSavings = totalsSavings / 365

  return (
    <SlideBase background={background}>
      {animate && <FadeBackground>
        <Group direction="vertical" grow verticalAlign="end" horizontalAlign="start" gap={12}>
          <Group direction="vertical" gap={4}>
            <Caption colour="highlight-primary" variant="small" format="uppercase">Enjoying More For Less</Caption>
            <Heading variant="heading5">
              <b>
                {heading}
              </b>
            </Heading>
          </Group>
          <Group direction="vertical" gap={4}>
            <Heading variant="heading1alt">
              <FormatCurrency value={savingsValue} currency={savingsCurrency} notation={useCompactFormat ? 'compact' : 'standard'} maximumFractionDigits={0} />
            </Heading>
            {!useTotalsFallback && (
              <BodyText variant="medium">
                And your next escape could save you even more.
              </BodyText>
            )}
            {useTotalsFallback && (
              <BodyText variant="medium">
                That's enough to save <FormatCurrency value={dailyTotalSavings} currency={savingsCurrency} notation="compact" maximumFractionDigits={1} /> every day, all year long.
              </BodyText>
            )}
          </Group>
        </Group>
      </FadeBackground>}
    </SlideBase>
  )
}

export default React.memo(SavingsSlide)
